import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useKeyboard } from './KeyboardContext';
import getIcon from '../../utils/Icon';

const QwertyKeyboard = () => {
    const { handleKeyPress, closeKeyboard, pasteFromClipboard } = useKeyboard();
    const [isShift, setIsShift] = useState(false);
    const [isSymbols, setIsSymbols] = useState(false);
    const [showSymbols2, setShowSymbols2] = useState(false);
    
    // Accelerating backspace — начинает медленно, ускоряется
    const bsTimer = useRef(null);
    const bsCount = useRef(0);

    const letterRows = [
        ['q', 'w', 'e', 'r', 't', 'y', 'u', 'i', 'o', 'p'],
        ['a', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l'],
        ['SHIFT', 'z', 'x', 'c', 'v', 'b', 'n', 'm', 'BACKSPACE'],
    ];

    const symbolRows = [
        ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'],
        ['-', '/', ':', ';', '(', ')', '$', '&', '@', '"'],
        ['SYMBOLS', '.', ',', '?', '!', "'", 'BACKSPACE'],
    ];

    const symbolRows2 = [
        ['[', ']', '{', '}', '#', '%', '^', '*', '+', '='],
        ['_', '\\', '|', '~', '<', '>', '€', '£', '¥', '•'],
        ['ABC', '.', ',', '?', '!', "'", 'BACKSPACE'],
    ];

    const getCurrentRows = () => {
        if (!isSymbols) return letterRows;
        return showSymbols2 ? symbolRows2 : symbolRows;
    };

    // Ускоряющийся backspace: начинает медленно (150мс), ускоряется до 25мс
    const scheduleNextBackspace = useCallback(() => {
        bsCount.current++;
        const count = bsCount.current;
        // Интервал уменьшается с каждым удалением: 150 → 100 → 70 → 50 → 35 → 25мс
        let delay;
        if (count < 3) delay = 150;
        else if (count < 6) delay = 100;
        else if (count < 10) delay = 70;
        else if (count < 18) delay = 50;
        else if (count < 30) delay = 35;
        else delay = 25;
        
        bsTimer.current = setTimeout(() => {
            handleKeyPress('BACKSPACE');
            scheduleNextBackspace();
        }, delay);
    }, [handleKeyPress]);

    const startBackspaceRepeat = useCallback(() => {
        bsCount.current = 0;
        // Первое повторение через 400мс (пауза перед ускорением)
        bsTimer.current = setTimeout(() => {
            handleKeyPress('BACKSPACE');
            scheduleNextBackspace();
        }, 400);
    }, [handleKeyPress, scheduleNextBackspace]);

    const stopBackspaceRepeat = useCallback(() => {
        if (bsTimer.current) {
            clearTimeout(bsTimer.current);
            bsTimer.current = null;
        }
        bsCount.current = 0;
    }, []);

    useEffect(() => {
        return () => stopBackspaceRepeat();
    }, [stopBackspaceRepeat]);

    const renderKey = (key, rowIndex) => {
        const isSpecial = ['SHIFT', 'BACKSPACE', 'SYMBOLS', 'ABC'].includes(key);
        const isShiftKey = key === 'SHIFT';
        const isBackspace = key === 'BACKSPACE';
        const isSymbolsKey = key === 'SYMBOLS';
        const isABCKey = key === 'ABC';

        let widthClass = 'flex-1';
        if (isSpecial) {
            widthClass = 'w-[44px]';
        }

        const handlePress = () => {
            if (isShiftKey) {
                setIsShift(!isShift);
                return;
            }
            if (isSymbolsKey) {
                setShowSymbols2(!showSymbols2);
                return;
            }
            if (isABCKey) {
                setIsSymbols(false);
                setShowSymbols2(false);
                return;
            }
            
            const keyToSend = isShift && !isSymbols ? key.toUpperCase() : key;
            handleKeyPress(keyToSend);
            
            if (isShift && !isSymbols && !isSpecial) {
                setIsShift(false);
            }
        };

        const displayKey = () => {
            if (isBackspace) {
                return (
                    <svg width="22" height="16" viewBox="0 0 28 20" fill="none">
                        <path 
                            d="M8.5 2L2 10L8.5 18H26V2H8.5Z" 
                            stroke="#EAEAEA" 
                            strokeWidth="2" 
                            strokeLinecap="round" 
                            strokeLinejoin="round"
                        />
                        <path 
                            d="M12 7L19 13M19 7L12 13" 
                            stroke="#EAEAEA" 
                            strokeWidth="2" 
                            strokeLinecap="round"
                        />
                    </svg>
                );
            }
            if (isShiftKey) {
                return (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                        <path 
                            d="M12 4L4 12H8V20H16V12H20L12 4Z" 
                            stroke={isShift ? "#2B8FF4" : "white"}
                            fill={isShift ? "#2B8FF4" : "none"}
                            strokeWidth="2"
                            strokeLinecap="round" 
                            strokeLinejoin="round"
                        />
                    </svg>
                );
            }
            if (isSymbolsKey) {
                return <span className="text-[12px] font-medium">#+=</span>;
            }
            if (isABCKey) {
                return <span className="text-[12px] font-medium">ABC</span>;
            }
            return (
                <span className="text-[18px]">
                    {isShift && !isSymbols ? key.toUpperCase() : key}
                </span>
            );
        };

        const getButtonStyle = () => {
            if (isShiftKey && isShift) {
                return {
                    background: 'rgba(43, 143, 244, 0.15)',
                    boxShadow: '0 2px 8px rgba(43, 143, 244, 0.2), inset 0 1px 0 rgba(234, 234, 234, 0.1)',
                    border: '1px solid rgba(43, 143, 244, 0.4)',
                };
            }
            if (isSpecial) {
                return {
                    background: 'linear-gradient(180deg, #3A3A3E 0%, #2A2A2E 100%)',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(234, 234, 234, 0.05)',
                };
            }
            return {
                background: 'linear-gradient(180deg, #4A4A4E 0%, #3A3A3E 100%)',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(234, 234, 234, 0.08)',
            };
        };

        // Backspace: long-press support
        const touchProps = isBackspace ? {
            onTouchStart: (e) => {
                handleKeyPress('BACKSPACE');
                startBackspaceRepeat();
            },
            onTouchEnd: stopBackspaceRepeat,
            onTouchCancel: stopBackspaceRepeat,
            onMouseDown: (e) => {
                handleKeyPress('BACKSPACE');
                startBackspaceRepeat();
            },
            onMouseUp: stopBackspaceRepeat,
            onMouseLeave: stopBackspaceRepeat,
        } : {};

        return (
            <button
                key={key + rowIndex}
                onClick={isBackspace ? undefined : handlePress}
                className={`${widthClass} h-[44px] rounded-[8px]
                    flex items-center justify-center
                    text-white
                    active:scale-[0.95] transition-all duration-100`}
                style={getButtonStyle()}
                {...touchProps}
            >
                {displayKey()}
            </button>
        );
    };

    const rows = getCurrentRows();

    return (
        <div className="w-full px-2 pb-14 ">
            {/* Letter/Symbol rows */}
            <div className="flex flex-col gap-[6px]">
                {rows.map((row, rowIndex) => (
                    <div 
                        key={rowIndex} 
                        className={`flex flex-row gap-[5px] ${rowIndex === 1 ? 'px-[12px]' : ''}`}
                    >
                        {row.map((key) => renderKey(key, rowIndex))}
                    </div>
                ))}
            </div>

            {/* Bottom row: 123/Paste | ◀ ▶ | space | apply */}
            <div className="flex flex-row gap-[6px] mt-[8px]">
                {/* 123 / ABC toggle */}
                <button
                    onClick={() => setIsSymbols(!isSymbols)}
                    className="w-[52px] h-[44px] rounded-[8px] flex items-center justify-center
                        active:scale-[0.95] transition-all duration-100"
                    style={{
                        background: 'linear-gradient(180deg, #3A3A3E 0%, #2A2A2E 100%)',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(234, 234, 234, 0.05)',
                    }}
                >
                    <span className="text-white text-[13px] font-medium">{isSymbols ? 'ABC' : '123'}</span>
                </button>
                
                {/* Paste button */}
                <button
                    onClick={pasteFromClipboard}
                    className="w-[44px] h-[44px] rounded-[8px] flex items-center justify-center
                        active:scale-[0.95] transition-all duration-100"
                    style={{
                        background: 'linear-gradient(180deg, #3A3A3E 0%, #2A2A2E 100%)',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(234, 234, 234, 0.05)',
                    }}
                    title="Paste"
                >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                        <path 
                            d="M16 4H18C19.1046 4 20 4.89543 20 6V20C20 21.1046 19.1046 22 18 22H6C4.89543 22 4 21.1046 4 20V6C4 4.89543 4.89543 4 6 4H8" 
                            stroke="#EAEAEA" 
                            strokeWidth="1.5" 
                            strokeLinecap="round"
                        />
                        <rect x="8" y="2" width="8" height="4" rx="1" stroke="#EAEAEA" strokeWidth="1.5"/>
                    </svg>
                </button>
                
                {/* Cursor Left */}
                <button
                    onClick={() => handleKeyPress('LEFT')}
                    className="w-[36px] h-[44px] rounded-[8px] flex items-center justify-center
                        active:scale-[0.95] transition-all duration-100"
                    style={{
                        background: 'linear-gradient(180deg, #3A3A3E 0%, #2A2A2E 100%)',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(234, 234, 234, 0.05)',
                    }}
                >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                        <path d="M15 18L9 12L15 6" stroke="#EAEAEA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                </button>
                
                {/* Cursor Right */}
                <button
                    onClick={() => handleKeyPress('RIGHT')}
                    className="w-[36px] h-[44px] rounded-[8px] flex items-center justify-center
                        active:scale-[0.95] transition-all duration-100"
                    style={{
                        background: 'linear-gradient(180deg, #3A3A3E 0%, #2A2A2E 100%)',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(234, 234, 234, 0.05)',
                    }}
                >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                        <path d="M9 18L15 12L9 6" stroke="#EAEAEA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                </button>
                
                {/* Space */}
                <button
                    onClick={() => handleKeyPress('SPACE')}
                    className="flex-1 h-[44px] rounded-[8px] flex items-center justify-center
                        active:scale-[0.95] transition-all duration-100"
                    style={{
                        background: 'linear-gradient(180deg, #4A4A4E 0%, #3A3A3E 100%)',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(234, 234, 234, 0.08)',
                    }}
                >
                    <span className="text-white/70 text-[14px]">space</span>
                </button>
                
                {/* OK / Apply */}
                <button
                    onClick={closeKeyboard}
                    className="w-[68px] h-[44px] rounded-[10px] flex items-center justify-center gap-1.5
                        active:scale-[0.95] transition-all duration-100"
                    style={{
                        background: '#2B8FF4',
                        boxShadow: '0 4px 16px rgba(43, 143, 244, 0.3)',
                    }}
                >
                    <span className="text-white text-[14px] font-bold">OK</span>
                </button>
            </div>
        </div>
    );
};

export default QwertyKeyboard;
